import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, Apple } from 'lucide-react';
import { googleCalendarUrl, calendarLocation } from '@shared/calendar/event-calendar.js';

/**
 * Add-to-calendar for the event.
 *
 * Two routes because there is no single one that works everywhere: Google
 * Calendar takes a URL, while Apple Calendar and Outlook want an .ics file.
 *
 * The builders moved to shared/calendar so the ticket email can send the same
 * invite — it cannot run JavaScript, so the server renders the .ics behind a
 * URL instead. Here the file is still generated in the browser, which means it
 * works offline and needs no endpoint.
 */
export default function AddToCalendar({ event }) {
  const { t } = useTranslation();
  if (!event?.event_date) return null;

  const details = {
    title: event.name || 'The Monday Festival',
    description: event.tagline || '',
    location: calendarLocation(event.venue, event.address),
    startsAt: event.starts_at,
    endsAt: event.ends_at,
    eventDate: event.event_date,
    timezone: event.timezone,
    url: window.location.origin,
  };

  /*
   * The invite comes from the server, not from a Blob in the page.
   *
   * iOS Safari ignores the download attribute on a blob: URL — the file is
   * built, the click does nothing, and Apple Calendar never opens. That was the
   * whole "add to calendar doesn't work on iOS" report.
   *
   * Plain text/calendar, which is what Safari and Outlook both understand from
   * a tapped link. The endpoint also takes `?inline=1` and `?dl=1`
   * (octet-stream, forcing a save) for the day a real device proves one of them
   * behaves better here — simulator probes could not answer it, because
   * opening a .ics URL through the system routes it to Calendar's subscription
   * flow before Safari ever fetches it.
   *
   * buildIcs still runs everywhere else that needs it — the server renders this
   * endpoint with the same builder.
   */
  const icsHref = `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/event.ics`;

  return (
    <div>
      <p className="label-tag text-white/40 text-[10px] mb-2.5">{t('success.calendarTitle')}</p>
      <div className="flex flex-wrap gap-2">
        <a
          href={googleCalendarUrl(details)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white
                     font-heading font-bold uppercase text-sm px-4 py-2.5 rounded-lg hover:border-brand/40 transition"
        >
          <CalendarPlus className="w-4 h-4" /> {t('success.googleCalendar')}
        </a>
        <a
          href={icsHref}
          className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white
                     font-heading font-bold uppercase text-sm px-4 py-2.5 rounded-lg hover:border-brand/40 transition"
        >
          <Apple className="w-4 h-4" /> {t('success.downloadIcs')}
        </a>
      </div>
    </div>
  );
}
